import { type FieldSpec, formatFieldValue } from '@fundline/shared/fields/values';
import { formatPhone } from '@fundline/shared/phone';

/**
 * The merchant timeline (skeleton for M3): field changes from field_value_history plus the
 * merchant's audit rows (created, reassigned, archived, phone corrected, tags). Messages, calls,
 * emails and documents join the same list in later milestones.
 */

export interface TimelineField extends FieldSpec {
  id: string;
  label: string;
}

export interface TimelineHistory {
  id: number;
  fieldId: string;
  oldValue: unknown;
  newValue: unknown;
  source: string;
  setByUserId: string | null;
  createdAt: string;
}

export interface TimelineAudit {
  id: number;
  action: string;
  actorType: string;
  actorId: string | null;
  data: unknown;
  createdAt: string;
}

export interface TimelineItem {
  key: string;
  at: string;
  kind: 'field' | 'merchant' | 'tag' | 'compliance';
  title: string;
  detail: string | null;
  /** Who did it: a person's name, "AI", "Import", "System". */
  by: string;
}

const SOURCE_LABELS: Record<string, string> = {
  ai: 'AI',
  import: 'Import',
  api: 'API',
  system: 'System',
};

function record(data: unknown): Record<string, unknown> {
  return typeof data === 'object' && data !== null && !Array.isArray(data)
    ? (data as Record<string, unknown>)
    : {};
}

function str(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

const SUPPRESSION_TITLES: Record<string, string> = {
  manual: 'Marked do not contact',
  do_not_contact: 'Marked do not contact',
  carrier_block: 'Blocked texts at their carrier',
  opt_out: 'Opted out',
  vendor_dnc: 'Marked do not contact by a lead vendor',
};

const CONSENT_SOURCES: Record<string, string> = {
  web_form: 'web form',
  vendor: 'lead vendor',
  verbal: 'verbal',
  written: 'written',
  other: 'other',
  sms_start: 'texted START',
  inbound_text: 'texted us first',
  import: 'import',
  api: 'API',
};

/** How an opt-out was recognized: "keyword:stop" → 'Texted "stop"'. */
function keywordSource(source: string | null): string | null {
  if (!source) return null;
  if (source.startsWith('keyword:')) return `Texted "${source.slice('keyword:'.length)}"`;
  if (source === 'telnyx_autoresponse') return "The carrier's STOP handling answered";
  return null;
}

/** Opt-outs, holds, resubscribes, suppression and consent (M6 audit actions). */
function complianceItem(
  action: string,
  d: Record<string, unknown>,
): { title: string; detail: string | null } | null {
  switch (action) {
    case 'opt_out.recorded':
      return { title: 'Opted out of texts', detail: keywordSource(str(d.source)) };
    case 'opt_out.repeated':
      return { title: 'Texted an opt-out word again', detail: keywordSource(str(d.source)) };
    case 'opt_out.review_hold':
      return {
        title: 'Texting on hold: possible opt-out',
        detail: str(d.keyword) ? `The text contained "${str(d.keyword)}"` : null,
      };
    case 'opt_out.released':
      return {
        title: 'Resubscribed by texting START',
        detail: d.still_blocked ? 'Still marked do not contact' : null,
      };
    case 'suppression.added':
      return {
        title: SUPPRESSION_TITLES[str(d.reason) ?? ''] ?? 'Added to the suppression list',
        detail: str(d.note),
      };
    case 'suppression.released':
      return { title: 'Do not contact lifted', detail: str(d.release_note) };
    case 'consent.recorded': {
      const source = str(d.source);
      return {
        title: 'Consent recorded',
        detail:
          [source ? (CONSENT_SOURCES[source] ?? source) : null, str(d.scope)]
            .filter(Boolean)
            .join(' · ') || null,
      };
    }
    default:
      return null;
  }
}

export function buildTimeline(input: {
  history: readonly TimelineHistory[];
  audit: readonly TimelineAudit[];
  fields: readonly TimelineField[];
  /** user id → display name */
  people: ReadonlyMap<string, string>;
}): TimelineItem[] {
  const fields = new Map(input.fields.map((f) => [f.id, f]));
  const person = (id: string | null | undefined) =>
    (id && input.people.get(id)) || (id ? 'A former member' : null);
  const items: TimelineItem[] = [];

  for (const h of input.history) {
    const field = fields.get(h.fieldId);
    const label = field?.label ?? 'A removed field';
    const fmt = (v: unknown) =>
      field ? formatFieldValue(field, v) : v === null || v === undefined ? '' : JSON.stringify(v);
    const to = fmt(h.newValue);
    const from = fmt(h.oldValue);
    items.push({
      key: `h${h.id}`,
      at: h.createdAt,
      kind: 'field',
      title: to === '' ? `${label} cleared` : `${label} set to ${to}`,
      detail: from === '' ? null : `Was ${from}`,
      by:
        h.source === 'rep'
          ? (person(h.setByUserId) ?? 'Someone')
          : (SOURCE_LABELS[h.source] ?? h.source),
    });
  }

  for (const a of input.audit) {
    const d = record(a.data);
    const by =
      a.actorType === 'user'
        ? (person(a.actorId) ?? 'Someone')
        : a.actorType === 'merchant'
          ? 'Merchant'
          : 'System';
    const base = { key: `a${a.id}`, at: a.createdAt, by };
    const compliance = complianceItem(a.action, d);
    if (compliance) {
      items.push({ ...base, kind: 'compliance', ...compliance });
      continue;
    }
    switch (a.action) {
      case 'merchant.created':
        items.push({ ...base, kind: 'merchant', title: 'Merchant added', detail: null });
        break;
      case 'merchant.reassigned':
        items.push({
          ...base,
          kind: 'merchant',
          title: `Assigned to ${person(str(d.to)) ?? 'someone'}`,
          detail: d.from ? `Was ${person(str(d.from)) ?? 'someone'}` : null,
        });
        break;
      case 'merchant.archived':
        items.push({ ...base, kind: 'merchant', title: 'Archived', detail: null });
        break;
      case 'merchant.unarchived':
        items.push({ ...base, kind: 'merchant', title: 'Restored from the archive', detail: null });
        break;
      case 'merchant.phone_changed': {
        const to = str(d.to);
        const from = str(d.from);
        items.push({
          ...base,
          kind: 'merchant',
          title: `Phone corrected to ${to ? formatPhone(to) : 'a new number'}`,
          detail: from ? `Was ${formatPhone(from)}` : null,
        });
        break;
      }
      case 'merchant.tag_added':
      case 'merchant.tag_removed': {
        const name = str(d.tag_name) ?? 'a deleted tag';
        const added = a.action === 'merchant.tag_added';
        const addedBy = str(d.added_by);
        const auto = added && addedBy && addedBy !== 'rep' ? addedBy : null;
        items.push({
          ...base,
          kind: 'tag',
          title: added ? `Tagged ${name}` : `Tag ${name} removed`,
          detail: null,
          by: auto ? (SOURCE_LABELS[auto] ?? auto) : by,
        });
        break;
      }
      default:
        // Actions from later milestones show up once they have a description here.
        break;
    }
  }

  // Newest first; ties keep a stable order (audit after history of the same instant).
  return items.sort((x, y) => (x.at === y.at ? x.key.localeCompare(y.key) : x.at < y.at ? 1 : -1));
}
